import React, { useRef, useState, useEffect, forwardRef } from 'react';
import Hls from 'hls.js';
import axiosInstance from '../../Config/axiosConfig';
import './VideoPlay.css';
import DeviceInfo from "../../Config/DeviceInfo";

const VideoPlay = forwardRef(({ videoId, onVideoEnd, autoPlay }, ref) => {
  const videoRef = useRef(null);
  const [accessToken, setAccessToken] = useState(null);
  const [streamToken, setStreamToken] = useState(null);
  const [deviceId, setDeviceId] = useState('');
  const [userIp, setUserIp] = useState('');

  // Expose video element to parent via ref
  React.useImperativeHandle(ref, () => ({
    play: () => {
      if (videoRef.current) {
        videoRef.current.play().catch(e => console.warn("Autoplay failed:", e));
      }
    }
  }));

  const requestStreamingToken = async (videoId) => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem("deviceId");
      const storedUserIp = localStorage.getItem("userIp");
      
      const response = await axiosInstance.get(`/Video/get-video-streaming-token/${videoId}`,
        {
          headers: {
            "Authorization": `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            "UserDevice": storedDeviceId,
            "UserIPAddress": storedUserIp
          }
        }
      );
      
      const data = response.data;
      if (data.code === 200 && data.data && data.data.jwt) {
        const token = data.data.jwt;
        localStorage.setItem("stream-token", token);
        return token;
      } else {
        throw new Error("Invalid login response");
      }
    } catch (error) {
      console.error("Failed to get streaming token:", error);
      throw error;
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");
    
    if (storedToken) setAccessToken(storedToken);
    if (storedDeviceId) setDeviceId(storedDeviceId);
    if (storedUserIp) setUserIp(storedUserIp);
  }, []);

  useEffect(() => {
    if (accessToken && deviceId && userIp && videoId) {
      const fetchStreamToken = async () => {
        try {
          const token = await requestStreamingToken(videoId);
          setStreamToken(token);
        } catch (error) {
          console.error("Error getting stream token:", error);
        }
      };
      
      fetchStreamToken();
    }
  }, [accessToken, deviceId, userIp, videoId]);

  useEffect(() => {
    if (streamToken && deviceId && userIp && videoId) {
      initializePlayer();
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [streamToken, deviceId, userIp, videoId]);

  const initializePlayer = () => {
    const video = videoRef.current;
    if (!video || !videoId) return;

// Use axiosInstance baseURL
  const baseURL = axiosInstance.defaults.baseURL?.replace(/\/+$/, ''); // remove any trailing slash
  const videoSrc = `${baseURL}/video/stream?videoId=${videoId}`;

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 100,
        maxMaxBufferLength: 200,
        autoStartLoad: true,
        enableSoftwareAES: true
      });

      hls.config.xhrSetup = function (xhr) {
        xhr.setRequestHeader('Authorization', `Bearer ${streamToken}`);
        xhr.setRequestHeader('UserDevice', deviceId);
        xhr.setRequestHeader('UserIPAddress', userIp);
      };

      hls.loadSource(videoSrc);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        if (autoPlay) {
          video.play().catch(e => console.warn("Autoplay failed:", e));
        }
      });

      video.addEventListener('ended', handleEnded);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoSrc;
      video.setAttribute('crossorigin', 'anonymous');
      video.load();
      video.addEventListener('ended', handleEnded);
      
      if (autoPlay) {
        video.play().catch(e => console.warn("Autoplay failed:", e));
      }
    }
  };

  const handleEnded = () => {
    if (onVideoEnd) {
      onVideoEnd();
    }
  };

  return (
    <div className="video-player-container">
      <DeviceInfo setDeviceId={setDeviceId} setUserIp={setUserIp} />
      <video 
        ref={videoRef} 
        className="video-player" 
        controls
        playsInline
        autoPlay={autoPlay}
      ></video>
    </div>
  );
});

export default VideoPlay;